import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { ArrowLeft, PackagePlus, Trash2 } from "lucide-react";
import SearchPicker from "../../../../components/admin/SearchPicker.jsx";
import { SelectDropdown, StatusBadge, TextInput, confirmDelete, showErrorAlert } from "../../../../components/ui/index.js";
import { useAuthContext } from "../../../../components/providers/AuthProvider.jsx";
import { canManageAllProjects } from "../../../../lib/permissions.js";
import { CUSTOMER_STATE, PROJECT_STATUS_OPTIONS } from "../../../../components/catalog/catalogUtils.js";
import ProjectItemEditor from "./ProjectItemEditor.jsx";
import AddItemsModal from "./AddItemsModal.jsx";
import SharePanel from "./SharePanel.jsx";

const leadLabel = (lead) => lead?.company_name || lead?.contact_name || lead?.email || lead?.phone || (lead ? `Lead #${lead.id}` : "");
const clienteLabel = (cliente) => cliente?.company_name || [cliente?.nombre, cliente?.apellido].filter(Boolean).join(" ") || cliente?.email || "";
const userLabel = (user) => (user ? (user.name && user.name !== user.email ? `${user.name} (${user.email})` : user.email) : "");

const formFrom = (project) => ({
    name: project.name,
    description: project.description || "",
    status: project.status,
    lead: project.lead,
    lead_label: project.lead_name,
    cliente: project.cliente,
    cliente_label: project.cliente_name,
    customer_user: project.customer_user,
    customer_user_label: project.customer_user_email,
    assigned_to: project.assigned_to,
    assigned_to_label: project.assigned_to_name,
});

/** Ficha de un proyecto comercial en el panel (C3D-38). */
export default function ProyectoDetalle({ projectId, onBack, onDeleted }) {
    const authContext = useAuthContext();
    const seesAll = canManageAllProjects(authContext);
    const [project, setProject] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formErrors, setFormErrors] = useState(null);
    const [adding, setAdding] = useState(false);

    const load = useCallback(() => {
        setLoadError(null);
        return axios.get(`/projects/${projectId}/`)
            .then(({ data }) => { setProject(data); setForm(formFrom(data)); })
            .catch((error) => setLoadError(error?.response?.status === 404 ? "not_found" : "network"));
    }, [projectId]);
    useEffect(() => { load(); }, [load]);

    // Recargar solo los datos (elementos, estado) sin pisar lo que se está editando arriba.
    const refresh = useCallback(() => axios.get(`/projects/${projectId}/`).then(({ data }) => setProject(data)), [projectId]);

    const existing = useMemo(() => new Set((project?.items || []).map((item) => (
        item.kind === "design" ? `design:${item.design_id}` : `product:${item.product_id}`
    ))), [project?.items]);

    if (loadError) {
        return (
            <div className="p-4 flex flex-col items-start gap-3">
                <p role="alert" className="text-error">
                    {loadError === "not_found" ? "Este proyecto no existe o no lo llevas tú." : "No se ha podido cargar el proyecto."}
                </p>
                <div className="flex gap-2">
                    <button type="button" className="btn btn-sm" onClick={onBack}>Volver a proyectos</button>
                    {loadError !== "not_found" && <button type="button" className="btn btn-sm btn-primary" onClick={load}>Reintentar</button>}
                </div>
            </div>
        );
    }
    if (!project || !form) {
        return (
            <div className="p-4 grid gap-4 lg:grid-cols-[1fr_22rem]" aria-busy="true">
                <div className="skeleton h-96 rounded-xl" />
                <div className="skeleton h-72 rounded-xl" />
            </div>
        );
    }

    const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
    const dirty = JSON.stringify(form) !== JSON.stringify(formFrom(project));
    const state = CUSTOMER_STATE[project.customer_state] || CUSTOMER_STATE.lead;

    const save = async () => {
        setSaving(true);
        setFormErrors(null);
        try {
            const payload = {
                name: form.name,
                description: form.description,
                status: form.status,
                lead: form.lead || null,
                cliente: form.cliente || null,
                customer_user: form.customer_user || null,
                ...(seesAll ? { assigned_to: form.assigned_to || null } : {}),
            };
            const { data } = await axios.patch(`/projects/${projectId}/`, payload);
            setProject(data);
            setForm(formFrom(data));
        } catch (error) {
            setFormErrors(error?.response?.data || { detail: "No se ha podido guardar." });
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        const confirmed = await confirmDelete({
            title: "Borrar proyecto",
            text: "Se borra el proyecto y su enlace deja de abrir. Los diseños no se borran.",
            confirmButtonText: "Borrar proyecto",
        });
        if (!confirmed) return;
        try {
            await axios.delete(`/projects/${projectId}/`);
            onDeleted();
        } catch {
            showErrorAlert({ text: "No se ha podido borrar el proyecto." });
        }
    };

    const move = async (index, delta) => {
        const ids = project.items.map((item) => item.id);
        const target = index + delta;
        [ids[index], ids[target]] = [ids[target], ids[index]];
        const previous = project.items;
        setProject((prev) => ({ ...prev, items: ids.map((id) => prev.items.find((item) => item.id === id)) }));
        try {
            await axios.post(`/projects/${projectId}/items/reorder/`, { order: ids });
        } catch {
            setProject((prev) => ({ ...prev, items: previous }));
            showErrorAlert({ text: "No se ha podido cambiar el orden." });
        }
    };

    const fieldError = (field) => formErrors?.[field] && <span className="label-text-alt text-error mt-1">{String(formErrors[field])}</span>;
    const generalError = formErrors && (formErrors.detail || formErrors.non_field_errors);

    return (
        <div className="p-2 sm:p-4 flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <button type="button" className="btn btn-ghost btn-sm gap-2" onClick={onBack}>
                    <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Proyectos
                </button>
                <div className="flex items-center gap-2">
                    <StatusBadge label={state.label} tone={state.tone} title={state.title} />
                    <button type="button" className="btn btn-ghost btn-sm gap-1 text-error" onClick={remove}>
                        <Trash2 className="w-4 h-4" aria-hidden="true" /> Borrar
                    </button>
                </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-[1fr_22rem] items-start">
                <div className="flex flex-col gap-4 min-w-0">
                    <section className="rounded-xl border border-base-content/10 bg-base-100 p-4 grid gap-4 md:grid-cols-2">
                        <label className="form-control md:col-span-2">
                            <span className="label-text text-sm font-medium">Nombre del proyecto</span>
                            <TextInput value={form.name} onChange={(event) => set("name", event.target.value)} />
                            {fieldError("name")}
                        </label>
                        <label className="form-control md:col-span-2">
                            <span className="label-text text-sm font-medium">Texto de presentación</span>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                rows={2}
                                value={form.description}
                                onChange={(event) => set("description", event.target.value)}
                            />
                            <span className="label-text-alt text-base-content/60 mt-1">Sale arriba del catálogo y en el PDF.</span>
                        </label>
                        <div className="form-control">
                            <span className="label-text text-sm font-medium mb-1">Estado</span>
                            <SelectDropdown options={PROJECT_STATUS_OPTIONS} value={form.status} onChange={(value) => set("status", value)} className="w-full" />
                        </div>
                        <div className="form-control">
                            {seesAll ? (
                                <SearchPicker
                                    label="Comercial"
                                    value={form.assigned_to}
                                    selectedLabel={form.assigned_to_label}
                                    endpoint="/projects/people-options/"
                                    paramsForQuery={(query) => ({ kind: "sales", search: query || undefined })}
                                    getOptionLabel={userLabel}
                                    onSelect={(option) => setForm((prev) => ({ ...prev, assigned_to: option?.id || null, assigned_to_label: userLabel(option) }))}
                                />
                            ) : (
                                <>
                                    <span className="label-text text-sm font-medium mb-1">Comercial</span>
                                    <span className="text-sm py-2">{project.assigned_to_name || "Tú"}</span>
                                </>
                            )}
                        </div>

                        <div className="md:col-span-2 grid gap-4 md:grid-cols-3 pt-2 border-t border-base-content/10">
                            <div className="form-control">
                                <SearchPicker
                                    label="Lead"
                                    value={form.lead}
                                    selectedLabel={form.lead_label}
                                    endpoint="/leads/"
                                    getOptionLabel={leadLabel}
                                    getOptionDescription={(lead) => lead.email || lead.phone}
                                    onSelect={(option) => setForm((prev) => ({ ...prev, lead: option?.id || null, lead_label: leadLabel(option) }))}
                                />
                                {fieldError("lead")}
                            </div>
                            <div className="form-control">
                                <SearchPicker
                                    label="Cliente"
                                    value={form.cliente}
                                    selectedLabel={form.cliente_label}
                                    endpoint="/clients/"
                                    getOptionLabel={clienteLabel}
                                    getOptionDescription={(cliente) => cliente.email}
                                    onSelect={(option) => setForm((prev) => ({ ...prev, cliente: option?.id || null, cliente_label: clienteLabel(option) }))}
                                />
                                {fieldError("cliente")}
                            </div>
                            <div className="form-control">
                                <SearchPicker
                                    label="Usuario registrado"
                                    value={form.customer_user}
                                    selectedLabel={form.customer_user_label}
                                    endpoint="/projects/people-options/"
                                    paramsForQuery={(query) => ({ kind: "customer", search: query || undefined })}
                                    getOptionLabel={userLabel}
                                    onSelect={(option) => setForm((prev) => ({ ...prev, customer_user: option?.id || null, customer_user_label: option?.email || "" }))}
                                />
                                <span className="label-text-alt text-base-content/60 mt-1">Lo verá en su perfil si no está en borrador.</span>
                            </div>
                        </div>

                        {generalError && <p role="alert" className="md:col-span-2 text-sm text-error">{String(generalError)}</p>}
                        <div className="md:col-span-2 flex justify-end">
                            <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={!dirty || saving || !form.name.trim()}>
                                {saving && <span className="loading loading-spinner loading-xs" aria-hidden="true" />}
                                Guardar proyecto
                            </button>
                        </div>
                    </section>

                    <section className="flex flex-col gap-3">
                        <div className="flex items-center justify-between gap-2">
                            <h2 className="text-lg font-semibold">
                                Modelos <span className="text-base-content/60 font-normal">({project.items.length})</span>
                            </h2>
                            <button type="button" className="btn btn-primary btn-sm gap-2" onClick={() => setAdding(true)}>
                                <PackagePlus className="w-4 h-4" aria-hidden="true" /> Añadir modelos
                            </button>
                        </div>
                        {project.items.length === 0 ? (
                            <div className="flex flex-col items-center text-center gap-2 py-12 rounded-xl border border-dashed border-base-content/20">
                                <p className="font-semibold">Este proyecto todavía no tiene modelos</p>
                                <p className="text-sm text-base-content/70 max-w-sm">
                                    Añade diseños de los customizadores (llaveros, posavasos, servilleteros) o productos de la tienda.
                                </p>
                                <button type="button" className="btn btn-sm btn-outline mt-2" onClick={() => setAdding(true)}>Añadir modelos</button>
                            </div>
                        ) : project.items.map((item, index) => (
                            <ProjectItemEditor
                                key={item.id}
                                projectId={project.id}
                                item={item}
                                isFirst={index === 0}
                                isLast={index === project.items.length - 1}
                                onMove={(delta) => move(index, delta)}
                                onChanged={refresh}
                            />
                        ))}
                    </section>
                </div>

                <aside className="lg:sticky lg:top-2 flex flex-col gap-4">
                    <SharePanel project={project} customerPhone={project.customer_phone} />
                </aside>
            </div>

            {adding && (
                <AddItemsModal
                    projectId={project.id}
                    existing={existing}
                    onClose={() => setAdding(false)}
                    onAdded={() => { setAdding(false); refresh(); }}
                />
            )}
        </div>
    );
}

ProyectoDetalle.propTypes = {
    projectId: PropTypes.number.isRequired,
    onBack: PropTypes.func.isRequired,
    onDeleted: PropTypes.func.isRequired,
};
